import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import { Search, Plus, Edit, Trash2, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { queryParams } from '../services/api';

export default function PurchaseRequestsPage() {
  const { user, hasPermission } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    request_date: new Date().toISOString().split('T')[0],
    requested_by: user?.id || '',
    notes: ''
  });
  const [items, setItems] = useState([{ product_id: '', quantity: '', notes: '' }]);

  const limit = 10;

  useEffect(() => {
    fetchRequests();
  }, [page, search]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = queryParams({
        page,
        limit,
        search,
        requested_by: user?.id,
        status: 'pending'
      });
      const res = await api.get('/purchase-requests', { params });
      if (res.data.success) {
        setRequests(res.data.data || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch {
      toast.error('Gagal memuat data purchase request');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        items,
        requested_by: user?.id
      };
      const res = await api.post('/purchase-requests', payload);
      if (res.data.success) {
        toast.success('Purchase request berhasil dibuat');
        setShowCreateModal(false);
        resetForm();
        fetchRequests();
      }
    } catch {
      toast.error('Gagal membuat purchase request');
    }
  };

  const resetForm = () => {
    setFormData({
      request_date: new Date().toISOString().split('T')[0],
      requested_by: user?.id || '',
      notes: ''
    });
    setItems([{ product_id: '', quantity: '', notes: '' }]);
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: '', notes: '' }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleEdit = (request) => {
    setSelectedRequest(request);
    setFormData({
      request_date: request.request_date,
      requested_by: request.requested_by,
      notes: request.notes || ''
    });
    setItems(request.items?.map(item => ({
      product_id: item.product_id || '',
      quantity: item.quantity,
      notes: item.notes || ''
    })) || [{ product_id: '', quantity: '', notes: '' }]);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Yakin ingin menghapus purchase request ini?')) return;
    try {
      await api.delete(`/purchase-requests/${id}`);
      toast.success('Purchase request berhasil dihapus');
      fetchRequests();
    } catch {
      toast.error('Gagal menghapus purchase request');
    }
  };

  const renderSkeleton = () => (
    <div className="card p-6">
      <div className="space-y-4">
        {[...Array(limit)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <div className="skeleton h-4 w-24" />
            <div className="skeleton h-4 w-32" />
            <div className="skeleton h-4 w-20" />
            <div className="skeleton h-4 w-16" />
            <div className="skeleton h-8 w-16" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Purchase Request</h1>
          <p className="page-subtitle">Buat permintaan pembelian berdasarkan kebutuhan barang</p>
        </div>
        {hasPermission('create_purchase_requests') && (
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus size={16} className="mr-2" /> Tambah Purchase Request
          </button>
        )}
      </div>

      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Cari nomor PO, supplier..."
              value={search}
              onChange={handleSearch}
              className="input pl-10 w-64"
            />
          </div>
          <div className="text-sm text-gray-500">
            Menampilkan {requests.length > 0 ? ((page - 1) * limit + 1) : 0} - {Math.min(page * limit, total)} dari {total} data
          </div>
        </div>

        {loading ? renderSkeleton() : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nomor Permintaan</th>
                  <th>Tanggal</th>
                  <th>Requested By</th>
                  <th>Total Items</th>
                  <th>Status</th>
                  <th className="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      <p className="mb-2">Belum ada data purchase request</p>
                    </td>
                  </tr>
                ) : (
                  requests.map((request, index) => (
                    <tr key={request.id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td className="font-medium">{request.pr_number}</td>
                      <td>{formatDate(request.request_date)}</td>
                      <td>{request.requestedBy?.full_name}</td>
                      <td>{request.total_items}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(request.status)}`}>{statusLabel(request.status)}</span>
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => { setSelectedRequest(request); setShowDetailModal(true); }} className="p-1 text-gray-600 hover:text-primary-800" title="Lihat Detail">
                            <Eye size={14} />
                          </button>
                          {hasPermission('edit_purchase_requests') && request.status === 'draft' && (
                            <button onClick={() => handleEdit(request)} className="p-1 text-gray-600 hover:text-primary-900" title="Edit">
                              <Edit size={14} />
                            </button>
                          )}
                          {hasPermission('delete_purchase_requests') && request.status === 'draft' && (
                            <button onClick={() => handleDelete(request.id)} className="p-1 text-gray-600 hover:text-danger-600" title="Hapus">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {total > limit && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div></div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm">Halaman {page} dari {Math.ceil(total / limit)}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / limit)} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showDetailModal) && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {showDetailModal ? 'Edit Purchase Request' : 'Buat Purchase Request Baru'}
                </h2>
                <button onClick={() => { setShowCreateModal(false); setShowDetailModal(false); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tanggal Request</label>
                  <input
                    type="date"
                    value={formData.request_date}
                    onChange={e => setFormData({...formData, request_date: e.target.value})}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="label">Requested By</label>
                  <input
                    type="text"
                    value={user?.fullName}
                    className="input bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    disabled
                  />
                </div>
              </div>

              <div>
                <label className="label">Catatan</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  className="input min-h-[80px]"
                  placeholder="Catatan tambahan..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="label">Items</label>
                  <button type="button" onClick={addItem} className="text-sm text-primary-800 hover:text-primary-900">
                    + Tambah Item
                  </button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Produk</label>
                        <select
                          value={item.product_id}
                          onChange={e => updateItem(index, 'product_id', e.target.value)}
                          className="input text-sm"
                          required
                        >
                          <option value="">Pilih Produk...</option>
                          {/* Produk options would be fetched from API */}
                          <option value="1">Laptop Dell XPS 13</option>
                          <option value="2">Mouse Wireless</option>
                        </select>
                      </div>
                      <div className="w-24">
                        <label className="text-xs text-gray-500">Kuantitas</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={e => updateItem(index, 'quantity', parseInt(e.target.value) || '')}
                          className="input text-sm"
                          min="1"
                          required
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Catatan</label>
                        <input
                          type="text"
                          value={item.notes}
                          onChange={e => updateItem(index, 'notes', e.target.value)}
                          className="input text-sm"
                          placeholder="Catatan..."
                        />
                      </div>
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeItem(index)} className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowCreateModal(false); setShowDetailModal(false); }} className="btn-secondary flex-1">
                  Batal
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {showDetailModal ? 'Perbarui' : 'Buat'} Purchase Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Detail Purchase Request</h2>
                <button onClick={() => { setShowDetailModal(false); setSelectedRequest(null); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">No: {selectedRequest.pr_number}</p>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tanggal Request</label>
                  <p className="text-gray-900 dark:text-white">{formatDate(selectedRequest.request_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Requested By</label>
                  <p className="text-gray-900 dark:text-white">{selectedRequest.requestedBy?.full_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`badge ${statusBadgeClass(selectedRequest.status)}`}>{statusLabel(selectedRequest.status)}</span>
                </div>
              </div>

              {selectedRequest.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Catatan</label>
                  <p className="text-gray-900 dark:text-white">{selectedRequest.notes}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500 mb-3 block">Items ({selectedRequest.total_items || 0})</label>
                <div className="space-y-2">
                  {selectedRequest.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">Product {item.product_id}</p>
                        <p className="text-xs text-gray-500">SKU: PROD-{item.product_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">Qty: {item.quantity}</p>
                        {item.notes && (
                          <p className="text-xs text-gray-500">{item.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
